import GAIASignInRequiredToBrowseWrapper from "@gaiaProfessionals/Components/HCP/GAIASignInRequiredToBrowseWrapper";
import { CustomMeetYourHerbsState } from "@gaiaProfessionals/Store/MeetYourHerbs/GAIAMeetYourHerbsReducer";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomMeetYourHerbsState) => {
    const productData = state.customMeetYourHerbsReducer?.productData;
    return {
        documents: productData?.documents
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface GAIAMYHProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    viewAllGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    authorNameText?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
}

export const documentsStyles: GAIAMYHProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 2rem;
            background-color: #f0f4f3;
        `,
    },
    container: {
        offsetCss: css`
            justify-content: center;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    viewAllGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        color: "primary",
        css: css`
            vertical-align: super;
            font-family: "LibreBaskerville";
            font-size: 18px;
            text-transform: capitalize;
        `,
    },
    authorNameText: {
        css: css`
            vertical-align: super;
            font-family: "LatoBold";
            font-size: 1rem;
            text-transform: capitalize;
            cursor: default;
        `,
    },
    title: {
        variant: "h2",
        css: css`
            color: #183028;
            width: 100%;
            font-size: 2rem;
            padding: 0px 25px 14px 25px;
            text-align: center;
            margin-bottom: 1rem;
        `,
    },
};

const styles = documentsStyles;

const GAIAMYHProductDetailsDocuments: React.FC<Props> = ({ documents, fields }) => {
    const [showAll, setShowAll] = useState(false);
    if (!documents || documents.length === 0) {
        return null;
    }

    const filteredDocuments = useMemo(() => {
        if (showAll) {
            return documents;
        }
        return documents.slice(0, 6);
    }, [documents, showAll]);

    return (
        <GAIASignInRequiredToBrowseWrapper redirectToSignIn={false}>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.title}>{fields.title}</Typography>
                <GridContainer {...styles.container}>
                    {filteredDocuments.map(document => {
                        let isAuthorDoc = true;
                        let name = document.name.toLowerCase().split("by dr.");
                        if (name.length === 1) {
                            name = document.name.toLowerCase().split("by");
                            isAuthorDoc = false;
                        }
                        const documentName = name.length > 0 ? name[0] : "";
                        const authorName = name.length > 1 ? name[1] : "";

                        return (
                            <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                <Link
                                    href={document.filePath}
                                    target="_new"
                                    {...styles.documentLink}
                                    data-test-selector={`productDetails_${document.id}`}
                                >
                                    {/* <IconMemo {...styles.documentIcon} /> */}
                                    <Typography {...styles.documentNameText}>{documentName}</Typography>
                                </Link>
                                {authorName && isAuthorDoc ? (
                                    <Typography {...styles.authorNameText}>by Dr. {authorName}</Typography>
                                ) : (
                                    <Typography {...styles.authorNameText}>by {authorName}</Typography>
                                )}
                            </GridItem>
                        );
                    })}
                    {documents.length > 6 && (
                        <GridItem {...styles.viewAllGridItem}>
                            <Button variant="secondary" onClick={() => setShowAll(!showAll)}>
                                {!showAll ? translate("View All") : translate("View Less")}
                            </Button>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledWrapper>
        </GAIASignInRequiredToBrowseWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(GAIAMYHProductDetailsDocuments),
    definition: {
        displayName: "GAIAMYH Documents",
        group: "Product Details",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "title",
                editorTemplate: "TextField",
                defaultValue: "Related Whitepapers",
                fieldType: "General",
            },
        ]
    },
};

export default widgetModule;
